/**
 * Pixi-backed floating-combat-text manager (#48).
 *
 * Owns `Text` objects on the `fx` layer, spawns one per incoming wire event,
 * animates them via the pure `advanceFloater` lifecycle, and reclaims them
 * when expired. Pools Text instances (reuse, don't allocate per number) and
 * enforces `style.maxConcurrent` by evicting the oldest floater — both are
 * the real perf levers called out in #48 (client-side cost is the only
 * meaningful one; server cost is negligible).
 *
 * Ingest is once-per-snapshot (events are a per-tick set), NOT per rendered
 * frame — otherwise a floater would re-spawn on every interpolated frame.
 * Animation runs per frame off wall-clock.
 *
 * #833: `ingestChat` is the same contract for the snapshot's SEPARATE `chat`
 * array. Chat floaters share this pool, this concurrency cap and this
 * eviction rule — the whole point of routing speech through the existing
 * system instead of building a second above-head label mechanism.
 */

import { Text, type Container } from "pixi.js";
import type { SeatIndex, WireEvent, WireSimState } from "@pit/protocol";
import {
  DEFAULT_COMBAT_TEXT_STYLE,
  advanceFloater,
  floaterColor,
  floaterText,
  type CombatTextStyle,
  type Floater,
} from "./combatText.js";
import { WORLD_TEXT_RESOLUTION, worldToScreen, type ProjectionCenter } from "./projection.js";

/** A pooled Text + its current floater binding (null when idle in the pool). */
interface Slot {
  readonly text: Text;
  floater: Floater | null;
}

/** Look up a target entity's current world position from a snapshot. */
export type PositionLookup = (
  targetId: WireEvent["target"],
) => { readonly x: number; readonly y: number } | null;

/**
 * #833: look up a SEAT's current world position from a snapshot. Chat is
 * attributed to a seat, not an entity (a message must stay attributable when
 * the speaker's body is dead or filtered out of this viewer's snapshot), so
 * chat needs its own lookup rather than reusing `PositionLookup`.
 *
 * Returning `null` — speaker absent from this snapshot, or hidden from this
 * viewer — SKIPS the floater. That is the "a dead/absent speaker must not
 * crash the renderer" contract, discharged by construction: there is nothing
 * to anchor to, so nothing is spawned.
 */
export type SeatPositionLookup = (
  seat: SeatIndex,
) => { readonly x: number; readonly y: number } | null;

export class CombatTextManager {
  private readonly layer: Container;
  private style: CombatTextStyle;
  private readonly slots: Slot[] = [];
  private nextId = 0;

  constructor(layer: Container, style: CombatTextStyle = DEFAULT_COMBAT_TEXT_STYLE) {
    this.layer = layer;
    this.style = style;
  }

  /** Swap the visual treatment at runtime (future settings UI). */
  setStyle(style: CombatTextStyle): void {
    this.style = style;
  }

  /**
   * Spawn floaters for this snapshot's events. `lookup` resolves the target's
   * world anchor (skip events whose target isn't visible). Call once per
   * arrived snapshot.
   */
  ingest(
    events: WireSimState["events"],
    lookup: PositionLookup,
    nowMs: number,
  ): void {
    for (const ev of events) {
      const at = lookup(ev.target);
      if (at === null) continue;
      this.spawn(ev, at.x, at.y, nowMs);
    }
  }

  /**
   * #833: spawn floaters for this snapshot's chat messages. `lookup` resolves
   * the SPEAKING SEAT's world anchor (skip messages whose speaker isn't
   * visible / isn't present). Call once per arrived snapshot, exactly like
   * `ingest` — the chat array has the same fresh-per-tick, drained,
   * never-repeated contract `events` has.
   *
   * A separate method rather than an `ingest` overload because the two arrays
   * key their anchor off different things (entity id vs. seat) — the same
   * reason they are separate arrays on the wire.
   */
  ingestChat(
    messages: WireSimState["chat"],
    lookup: SeatPositionLookup,
    nowMs: number,
  ): void {
    for (const m of messages) {
      const at = lookup(m.seat);
      if (at === null) continue;
      this.spawnFloater({
        id: this.nextId++,
        kind: "chat",
        amount: 0,
        ccClass: null,
        chatText: m.text,
        worldX: at.x,
        worldY: at.y,
        spawnedMs: nowMs,
      });
    }
  }

  private spawn(ev: WireEvent, worldX: number, worldY: number, nowMs: number): void {
    // #345/#463: `ccApplied`/`ccRemoved` carry `ccClass`, not `amount` — the
    // two are mutually exclusive per `WireEvent`'s discriminated union.
    const isCcEvent = ev.kind === "ccApplied" || ev.kind === "ccRemoved";
    this.spawnFloater({
      id: this.nextId++,
      kind: ev.kind,
      amount: isCcEvent ? 0 : ev.amount,
      ccClass: isCcEvent ? ev.ccClass : null,
      // #833: chat text is the chat kind's payload only.
      chatText: null,
      worldX,
      worldY,
      spawnedMs: nowMs,
    });
  }

  /** Bind a built `Floater` to a pooled slot and apply the style. The one
   *  place a floater becomes visible, shared by both ingest paths (#833). */
  private spawnFloater(floater: Floater): void {
    const slot = this.acquireSlot();
    slot.floater = floater;
    slot.text.text = floaterText(floater);
    slot.text.style.fill = floaterColor(floater.kind, this.style);
    slot.text.style.fontSize = this.style.fontSizePx;
    slot.text.style.fontWeight = String(this.style.fontWeight) as never;
    slot.text.visible = true;
  }

  /** Get a free slot, reusing an idle one; else make one; else evict oldest. */
  private acquireSlot(): Slot {
    // Reuse an idle slot.
    for (const s of this.slots) {
      if (s.floater === null) return s;
    }
    // Grow until the concurrency cap.
    if (this.slots.length < this.style.maxConcurrent) {
      const text = new Text({
        text: "",
        style: { fontFamily: "sans-serif" },
        // #730: world text is magnified by the viewport cover zoom — supersample
        // so it stays crisp. See WORLD_TEXT_RESOLUTION.
        resolution: WORLD_TEXT_RESOLUTION,
      });
      text.anchor.set(0.5, 0.5);
      text.visible = false;
      this.layer.addChild(text);
      const slot: Slot = { text, floater: null };
      this.slots.push(slot);
      return slot;
    }
    // At cap (≥ 1): every slot is live — evict the oldest (lowest id).
    let oldest = this.slots[0];
    if (oldest === undefined) {
      // Unreachable: cap ≥ 1 guarantees at least one slot. Grow rather than
      // crash if a zero-cap style is ever configured.
      const text = new Text({
        text: "",
        style: { fontFamily: "sans-serif" },
        // #730: world text is magnified by the viewport cover zoom — supersample
        // so it stays crisp. See WORLD_TEXT_RESOLUTION.
        resolution: WORLD_TEXT_RESOLUTION,
      });
      text.anchor.set(0.5, 0.5);
      text.visible = false;
      this.layer.addChild(text);
      const slot: Slot = { text, floater: null };
      this.slots.push(slot);
      return slot;
    }
    for (const s of this.slots) {
      const of = oldest.floater;
      if (s.floater !== null && (of === null || s.floater.id < of.id)) {
        oldest = s;
      }
    }
    return oldest;
  }

  /**
   * Animate every live floater to `nowMs` and reclaim expired ones. Positions
   * project the world anchor through the camera `center`, then apply the
   * upward screen rise + the style's spawn offset.
   */
  update(nowMs: number, center: ProjectionCenter): void {
    for (const s of this.slots) {
      const f = s.floater;
      if (f === null) continue;
      const frame = advanceFloater(f, nowMs, this.style);
      if (frame.expired) {
        s.floater = null;
        s.text.visible = false;
        continue;
      }
      const screen = worldToScreen(f.worldX, f.worldY, center);
      s.text.x = screen.sx;
      s.text.y = screen.sy - this.style.offsetAbovePx - frame.risePx;
      s.text.alpha = frame.opacity;
    }
  }

  /** Live floater count (for tests / diagnostics). */
  liveCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.floater !== null) n++;
    return n;
  }

  /** Destroy all Pixi resources. */
  destroy(): void {
    for (const s of this.slots) s.text.destroy();
    this.slots.length = 0;
  }
}
